import React, { useState } from "react";
import ContractInfoOverviewSection from "components/document/read/analysis/ContractInfoDetail/ContractInfoOverviewSection";
import useDocumentByAdminQuery from "features/document/hooks/queries/detail/useDocumentByAdminQuery";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import ContractInfoReviewResultSection from "components/document/read/analysis/ContractInfoDetail/ContractInfoReviewResultSection";
import AnalysisContinuousPreview from "components/document/read/analysis/ContractInfoDetail/AnalysisContinuousPreview";
import DocumentDetail from "components/document/read/common/DocumentDetail";
import { FormProvider, useForm } from "react-hook-form";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import DocumentDetailFooterForDecisionManager from "components/document/read/common/footer/DocumentDetailFooterForDecisionManager";
import usePDF from "features/pdf/hooks/usePDF";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import DocumentViewer from "components/document/read/DocumentViewer";
import {
  DocumentDetailResponse,
  DocumentDto,
  UserDetailResponse,
} from "apis/data-contracts";
import { pdf, View } from "@react-pdf/renderer";
import { SignInfo } from "components/document/read/common/DocumentDetailSignSection";
import { useRecoilValue } from "recoil";
import { showApproverSignState } from "store/document/documentCommon";
import useDocumentDetailQuery from "features/document/hooks/queries/detail/useDocumentDetailQuery";
import CardLayout from "components/foundation/templates/CardLayout";

const Doc = ({
  adminDocResponse,
  currentUser,
  approvedUserInfo,
  showApproverSign,
  approveComment,
  webPreview,
}: {
  adminDocResponse: DocumentDto;
  currentUser?: UserDetailResponse;
  approvedUserInfo?: SignInfo;
  showApproverSign: boolean;
  approveComment?: string;
  webPreview?: boolean;
}) => {
  return (
    <DocumentViewer
      formComponent={
        <View
          style={{
            display: "flex",
            gap: "5mm",
          }}
        >
          <ContractInfoOverviewSection
            documentData={adminDocResponse.documentData}
          />
          {/* 1단계 결재요청 상태일 때 표시하지 않음 */}
          {adminDocResponse.documentApprove !== "REVIEW_IN_PROGRESS" && (
            <ContractInfoReviewResultSection
              documentData={adminDocResponse.documentData}
            />
          )}
        </View>
      }
      companyName={adminDocResponse.companyName}
      documentClassName={adminDocResponse.documentClassDto.name}
      documentApproveStatus={
        adminDocResponse.documentApprove as DocumentDetailResponse["approveStatus"]
      }
      compactHeader
      flowFooter={webPreview}
      tags={adminDocResponse.tagList}
      createdUserInfo={{
        name: adminDocResponse.author,
        signImageUrl: adminDocResponse.authorSignImageUrl,
        date: adminDocResponse.createdAt,
      }}
      approvedUserInfo={
        showApproverSign
          ? currentUser && {
              name: currentUser.name,
              signImageUrl: currentUser.signImageUrl,
              date: new Date().toISOString(),
            }
          : approvedUserInfo
      }
      showApproverSign={showApproverSign}
      attachmentList={adminDocResponse.attachments}
      approveComment={
        approveComment ||
        adminDocResponse?.additionalFields?.approveComment ||
        ""
      }
    />
  );
};

const AnalysisDetailPageForDecisionManager = () => {
  const documentUuid = useParams().uuid;

  const { data: adminDocResponse } = useDocumentByAdminQuery(documentUuid);
  const { data: documentData } = useDocumentDetailQuery(documentUuid);
  const approvedUser = documentData?.approvedUserDto?.uuid;
  const approvedUserName = documentData?.approvedUserDto?.name;
  const approvedUserSignImageUrl = documentData?.approvedUserDto?.signImageUrl;
  const approvedAt = documentData?.approveStatusUpdatedAt;
  const approvedUserInfo =
    approvedUserName && approvedAt
      ? {
          name: approvedUserName,
          signImageUrl: approvedUserSignImageUrl,
          date: approvedAt,
        }
      : undefined;

  /* 결재권자 정보 */
  const { data: currrentUser } = useUserDetailQuery();

  const methods = useForm();

  const { uploadDocPdfAndGetS3Url } = usePDF<HTMLDivElement>();

  const showApproverSign = useRecoilValue(showApproverSignState);

  const handleUploadPdf = async (fileName: string, approveComment?: string) => {
    const basePdfBlob = await pdf(
      <Doc
        adminDocResponse={adminDocResponse as DocumentDto}
        currentUser={currrentUser}
        approvedUserInfo={approvedUserInfo}
        showApproverSign={showApproverSign}
        approveComment={approveComment}
      />,
    ).toBlob();

    return uploadDocPdfAndGetS3Url(basePdfBlob)(fileName);
  };

  /* BeforeUnload 비활성화 */
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  return (
    <>
      <FormProvider {...methods}>
        <WrapStyle>
          {adminDocResponse && (
            <>
              <CardLayout.Container style={{ width: "100%" }}>
                <PreviewScrollArea>
                  <PreviewScrollContent>
                    <AnalysisContinuousPreview
                      documentData={adminDocResponse.documentData}
                      documentClassName={adminDocResponse.documentClassDto.name}
                      companyName={adminDocResponse.companyName}
                      documentApproveStatus={
                        adminDocResponse.documentApprove as DocumentDetailResponse["approveStatus"]
                      }
                      tags={adminDocResponse.tagList}
                      createdUserInfo={{
                        name: adminDocResponse.author,
                        signImageUrl: adminDocResponse.authorSignImageUrl,
                        date: adminDocResponse.createdAt,
                      }}
                      approvedUserInfo={
                        currrentUser && {
                          name: currrentUser.name,
                          signImageUrl: currrentUser.signImageUrl,
                          date: new Date().toDateString(),
                        }
                      }
                      showApproverSign={showApproverSign}
                      attachmentList={adminDocResponse.attachments}
                      approveComment={
                        adminDocResponse?.additionalFields?.approveComment || ""
                      }
                      showReviewSection={
                        adminDocResponse.documentApprove !==
                        "REVIEW_IN_PROGRESS"
                      }
                    />
                  </PreviewScrollContent>
                </PreviewScrollArea>
              </CardLayout.Container>

              {adminDocResponse.attachments.length > 0 && (
                <DocumentDetail.AttachmentList
                  attachmentList={adminDocResponse.attachments}
                />
              )}
            </>
          )}

          {documentUuid && (
            <DocumentDetailFooterForDecisionManager
              documentUuid={documentUuid}
              setBeforeUnloadDisable={setBeforeUnloadDisable}
              uploadDocPdfAndGetS3Url={handleUploadPdf}
              referenceUsers={adminDocResponse?.referenceUsers}
              permissionType={adminDocResponse?.permissionType}
              documentApprove={adminDocResponse?.documentApprove || ""}
              author={adminDocResponse?.authorUuid || ""}
              comment={adminDocResponse?.comment || ""}
              approvedUser={approvedUser}
              isAnalysis={true}
            />
          )}
        </WrapStyle>
      </FormProvider>

      <BeforeUnloadModal
        isDirty={methods.formState.isDirty}
        disable={beforeUnloadDisable}
      />
    </>
  );
};

export default AnalysisDetailPageForDecisionManager;

const WrapStyle = styled.div`
  width: 100.5rem;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 2rem;

  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.media.small} {
    background-color: ${({ theme }) => theme.color.background.white};
    width: 100%;
  }
`;

const PreviewScrollArea = styled.div`
  width: 100%;
  overflow: visible;

  ${({ theme }) => theme.media.small} {
    overflow-x: hidden;
    overflow-y: visible;
  }
`;

const PreviewScrollContent = styled.div`
  width: 100%;
  margin: 0 auto;

  ${({ theme }) => theme.media.small} {
    width: 100%;
    min-width: 0;
    margin: 0;
  }
`;
